'use client';
import { useCallback, useState, useSyncExternalStore } from 'react';
import { createClient } from '@/lib/supabase/client';
import {
  emptyLiveScoresState,
  mergeEventLiveScores,
  parseEventLiveScoresEnvelope,
  type LiveScoresState,
} from '../../libs/liveScores';

export interface EventLiveScoresView {
  byScheduleId: LiveScoresState['byScheduleId'];
  revision: number;
  topologyRevision: number;
  updatedAt: string | null;
  connected: boolean;
}
const EMPTY: EventLiveScoresView = {
  byScheduleId: {},
  revision: 0,
  topologyRevision: 0,
  updatedAt: null,
  connected: false,
};
const registry = new Map<string, EventFeed>();
const FALLBACK_MS = 60000;

/** One visible subscription per event, shared by every mounted public consumer. */
class EventFeed {
  state = emptyLiveScoresState();
  snapshot: EventLiveScoresView = EMPTY;
  listeners = new Set<() => void>();
  client = createClient();
  channel: ReturnType<ReturnType<typeof createClient>['channel']> | null = null;
  timer: ReturnType<typeof setInterval> | null = null;
  connected = false;
  snapshotReady = false;
  inaccessible = false;
  epoch = 0;
  inFlight = false;
  refreshQueued = false;
  constructor(readonly eventId: string) {}
  emit() {
    this.snapshot = {
      byScheduleId: this.state.byScheduleId,
      revision: this.state.revision,
      topologyRevision: this.state.topologyRevision,
      updatedAt: this.state.updatedAt,
      connected: this.connected,
    };
    for (const listener of this.listeners) listener();
  }
  visible = () => document.visibilityState !== 'hidden';
  fallback() {
    if (
      !this.timer &&
      (!this.connected || !this.snapshotReady) &&
      this.visible() &&
      !this.inaccessible
    ) {
      this.timer = setInterval(() => {
        const hadChannel = this.channel !== null;
        this.start();
        if (hadChannel || !this.connected) void this.refresh();
      }, FALLBACK_MS);
    }
  }
  clearTimer() {
    if (this.timer) clearInterval(this.timer);
    this.timer = null;
  }
  refresh = async () => {
    if (!this.visible() || this.inaccessible || !this.listeners.size) return;
    if (this.inFlight) {
      this.refreshQueued = true;
      return;
    }
    this.inFlight = true;
    const epoch = this.epoch;
    try {
      const { data, error } = await this.client.rpc('get_event_live_scores', {
        p_event_id: this.eventId,
      });
      if (epoch !== this.epoch || !this.listeners.size || !this.visible()) return;
      if (error) {
        this.snapshotReady = false;
        this.fallback();
        return;
      }
      if (data === null) {
        this.inaccessible = true;
        this.state = emptyLiveScoresState();
        this.stop();
        this.emit();
        return;
      }
      const envelope = parseEventLiveScoresEnvelope(data, this.eventId);
      if (envelope?.snapshot) {
        this.snapshotReady = true;
        this.state = mergeEventLiveScores(this.state, envelope);
        this.emit();
        if (this.connected) this.clearTimer();
      } else {
        this.snapshotReady = false;
        this.fallback();
      }
    } catch {
      if (epoch === this.epoch) {
        this.snapshotReady = false;
        this.fallback();
      }
    } finally {
      if (epoch === this.epoch) {
        this.inFlight = false;
        if (this.refreshQueued) {
          this.refreshQueued = false;
          void this.refresh();
        }
      }
    }
  };
  start = () => {
    if (this.channel || !this.visible() || this.inaccessible || !this.listeners.size) return;
    const epoch = this.epoch;
    this.channel = this.client
      .channel(`event-live-scores-${this.eventId}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'event_live_scores',
          filter: `event_id=eq.${this.eventId}`,
        },
        (payload) => {
          if (epoch !== this.epoch || !this.visible()) return;
          const envelope = parseEventLiveScoresEnvelope(payload.new, this.eventId);
          if (!envelope || envelope.revision <= this.state.revision) return;
          const gap = envelope.revision > this.state.revision + 1;
          const topologyChanged = envelope.topology_revision > this.state.topologyRevision;
          this.state = mergeEventLiveScores(this.state, envelope);
          this.emit();
          // A correction can return to the last published score, leaving an empty
          // delta while a snapshot taken between corrections still holds the old winner.
          if (gap || topologyChanged || envelope.payload.snapshot_required) void this.refresh();
        },
      )
      .subscribe((status) => {
        if (epoch !== this.epoch || !this.visible()) return;
        if (status === 'CLOSED') {
          this.stop();
          this.emit();
          this.fallback();
          return;
        }
        this.connected = status === 'SUBSCRIBED';
        this.emit();
        if (this.connected) {
          this.clearTimer();
          void this.refresh();
        } else this.fallback();
      });
    this.fallback();
  };
  stop = () => {
    this.epoch++;
    this.inFlight = false;
    this.refreshQueued = false;
    this.clearTimer();
    const channel = this.channel;
    this.channel = null;
    if (channel) void channel.unsubscribe();
    this.connected = false;
  };
  visibility = () => {
    if (this.visible()) this.start();
    else {
      this.stop();
      this.emit();
    }
  };
  online = () => {
    if (this.visible()) {
      this.start();
      void this.refresh();
    }
  };
  add(listener: () => void) {
    this.listeners.add(listener);
    if (this.listeners.size === 1) {
      document.addEventListener('visibilitychange', this.visibility);
      window.addEventListener('online', this.online);
      this.start();
    }
    return () => {
      this.listeners.delete(listener);
      if (!this.listeners.size) {
        this.stop();
        document.removeEventListener('visibilitychange', this.visibility);
        window.removeEventListener('online', this.online);
        if (registry.get(this.eventId) === this) registry.delete(this.eventId);
      }
    };
  }
}
export function useEventLiveScores(
  eventId: string,
  enabled = true,
): EventLiveScoresView & { refresh: () => Promise<void> } {
  const refresh = useCallback(async () => {
    if (enabled) await registry.get(eventId)?.refresh();
  }, [eventId, enabled]);
  const subscribe = useCallback(
    (listener: () => void) => {
      if (!enabled || !eventId) return () => {};
      let feed = registry.get(eventId);
      if (!feed) {
        feed = new EventFeed(eventId);
        registry.set(eventId, feed);
      }
      return feed.add(listener);
    },
    [eventId, enabled],
  );
  const getSnapshot = useCallback(
    () => (enabled ? (registry.get(eventId)?.snapshot ?? EMPTY) : EMPTY),
    [eventId, enabled],
  );
  const snapshot = useSyncExternalStore(subscribe, getSnapshot, () => EMPTY);
  const [retained, setRetained] = useState<{ eventId: string; view: EventLiveScoresView }>({
    eventId,
    view: EMPTY,
  });
  if (retained.eventId !== eventId) setRetained({ eventId, view: EMPTY });
  else if (enabled && snapshot !== EMPTY && retained.view !== snapshot)
    setRetained({ eventId, view: snapshot });
  const last = retained.eventId === eventId ? retained.view : EMPTY;
  if (!enabled) return { ...last, connected: false, refresh };
  return { ...(snapshot === EMPTY ? last : snapshot), refresh };
}
